import { FC, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import type { ScoringType, IndustryBand } from "@/lib/scoring/types";
import { HgIndustryInput } from "@/components/filters/Industries/HgIndustryInput";
import { SicInput } from "@/components/filters/Industries/SicInput";
import { NaicsInput } from "@/components/filters/Industries/NaicsInput";
import { NAICS_HELP_URL } from "@/components/filters/Industries/constants";
import { pluralize } from "@/utils/text";

export const initialBand: IndustryBand = { industryIds: [], score: 0 };
export const hasErrors = (bands: unknown[]) =>
  (bands as IndustryBand[]).some(
    (b) =>
      b.score <= 0 ||
      (!b.industryIds?.length &&
        !b.sicUs1987Ids?.length &&
        !b.naics2012Ids?.length)
  );
export const getBandSummary = (band: unknown) => {
  const b = band as IndustryBand;
  if (b.sicUs1987Ids?.length)
    return `${b.sicUs1987Ids.length} ${pluralize("SIC code", b.sicUs1987Ids.length)}`;
  if (b.naics2012Ids?.length)
    return `${b.naics2012Ids.length} ${pluralize("NAICS code", b.naics2012Ids.length)}`;
  if (b.industryIds?.length)
    return `${b.industryIds.length} ${pluralize("industry", b.industryIds.length)}`;
  return "—";
};

type IndustryFilterType = "industry" | "sic" | "naics";

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const IndustryEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands = criterion.industry ?? [];

  const detectFilterType = (): IndustryFilterType => {
    if (bands.some((b) => b.sicUs1987Ids?.length)) return "sic";
    if (bands.some((b) => b.naics2012Ids?.length)) return "naics";
    return "industry";
  };

  const [filterType, setFilterType] =
    useState<IndustryFilterType>(detectFilterType);

  const handleFilterTypeChange = (
    _: unknown,
    newType: IndustryFilterType | null
  ) => {
    if (!newType) return;
    setFilterType(newType);
    onChange({
      ...criterion,
      industry: bands.map((b) => ({
        score: b.score,
        industryIds: undefined,
        sicUs1987Ids: undefined,
        naics2012Ids: undefined,
      })),
    });
  };

  const updateBand = (index: number, patch: Partial<IndustryBand>) => {
    onChange({
      ...criterion,
      industry: bands.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      industry: bands.filter((_, i) => i !== index),
    });
  };

  const addBand = () => {
    const newBand: IndustryBand =
      filterType === "sic"
        ? { sicUs1987Ids: [], score: 0 }
        : filterType === "naics"
          ? { naics2012Ids: [], score: 0 }
          : { industryIds: [], score: 0 };
    onChange({ ...criterion, industry: [...bands, newBand] });
  };

  return (
    <Box sx={{ px: 2 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filterType}
        onChange={handleFilterTypeChange}
        sx={{
          mb: 1.5,
          "& .MuiToggleButton-root": { py: 0.5, px: 1.5, fontSize: 12 },
        }}
      >
        <ToggleButton value="industry">Industry</ToggleButton>
        <ToggleButton value="sic">SIC</ToggleButton>
        <ToggleButton value="naics">
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            NAICS
            <Tooltip
              title={
                <Typography variant="body2">
                  You can see the complete 2012 NAICS list by visiting{" "}
                  <Link
                    href={NAICS_HELP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="hover"
                  >
                    the official site.
                  </Link>
                </Typography>
              }
              placement="top"
            >
              <HelpOutlineIcon sx={{ fontSize: 14 }} />
            </Tooltip>
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>

      {bands.map((band, i) => (
        <Box
          key={i}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.5,
            mb: 1,
          }}
        >
          {filterType === "industry" && (
            <HgIndustryInput
              id={`industry-band-${i}`}
              selectedItems={band.industryIds ?? []}
              onIndustriesSelect={(industryIds) =>
                updateBand(i, { industryIds })
              }
            />
          )}
          {filterType === "sic" && (
            <SicInput
              id={`sic-band-${i}`}
              selectedItems={band.sicUs1987Ids ?? []}
              onSicSelect={(sicUs1987Ids) => updateBand(i, { sicUs1987Ids })}
            />
          )}
          {filterType === "naics" && (
            <NaicsInput
              id={`naics-band-${i}`}
              selectedItems={band.naics2012Ids ?? []}
              onNaicsSelect={(naics2012Ids) => updateBand(i, { naics2012Ids })}
            />
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <TextField
              size="small"
              type="number"
              label="Pts"
              error={band.score === 0}
              value={band.score === 0 ? "" : band.score}
              onChange={(e) =>
                updateBand(i, {
                  score: e.target.value === "" ? 0 : Number(e.target.value),
                })
              }
              inputProps={{ min: 0, step: 1 }}
              sx={{ width: 80 }}
            />
            <IconButton
              size="small"
              onClick={() => removeBand(i)}
              aria-label="Remove band"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600, mt: 0.5 }}
      >
        ADD BAND
      </Button>
    </Box>
  );
};
